import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API, useAuth, formatErr } from "@/context/AuthContext";
import { useSites } from "@/context/SiteContext";
import Shell from "@/components/Shell";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, IdentificationCard, PencilSimple, TrashSimple, MagnifyingGlass,
  Paperclip, DownloadSimple, Warning, CheckCircle, FileXls, Upload,
  CaretUp, CaretDown, CaretUpDown, FilePdf,
} from "@phosphor-icons/react";
import { useConfirm } from "@/components/ConfirmDialog";
import MeslekAutocomplete from "@/components/MeslekAutocomplete";

const empty = {
  name: "", tc_no: "", birth_date: "", birth_place: "",
  phone: "", position: "",
  team_id: "", site_id: "",
  hire_date: "", end_date: "",
  iban: "", personnel_file_received: false, personnel_file_path: "",
  is_retired: false,
  status: "aktif", notes: "",
};

// TR IBAN normalize/format
const cleanIban = (v) => (v || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
const prettyIban = (v) => cleanIban(v).replace(/(.{4})/g, "$1 ").trim();

// TR IBAN sadece uzunluk (TR + 24) — server-side validation değil, UX hint
const isIbanShapeValid = (v) => {
  const c = cleanIban(v);
  if (!c) return true; // boş kabul
  return /^TR\d{24}$/.test(c);
};

// TC No 11 hane numeric — soft check
const isTcShapeValid = (v) => {
  if (!v) return true;
  return /^\d{11}$/.test(v);
};

// Türkçe "Yazım Düzeni" (Title Case): her kelimenin ilk harfi büyük, kalanı küçük.
// Kısa bağlaç/edatları (ve, ile, de) küçük tutmuyoruz — isim olduğu için her kelime kapitalize edilir.
const toTitleCaseTR = (s) =>
  (s || "")
    .toLocaleLowerCase("tr-TR")
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toLocaleUpperCase("tr-TR") + w.slice(1))
    .join(" ");

export default function Employees() {
  const { user } = useAuth();
  const { sites, activeSiteId } = useSites();
  const askConfirm = useConfirm();
  const [rows, setRows] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("aktif");
  const [teamFilter, setTeamFilter] = useState("all");
  const [retiredFilter, setRetiredFilter] = useState("all");   // "all" | "yes" | "no"
  const [sort, setSort] = useState({ key: "name", dir: "asc" });
  const [uploading, setUploading] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  // Yeni yetki paradigmasında modüle erişebilen kullanıcı modülün tüm
  // işlemlerini (ekleme, güncelleme, silme) yapabilir. ModuleGuard route
  // seviyesinde zaten doğruluyor; burada ek bir kilit yok.
  const canManage = !!user;
  const isAdmin = !!user;
  const activeSites = useMemo(() => sites.filter((s) => s.status === "aktif"), [sites]);

  const load = async () => {
    const params = {};
    if (activeSiteId && activeSiteId !== "all") params.site_id = activeSiteId;
    if (status !== "all") params.status = status;
    if (teamFilter !== "all") params.team_id = teamFilter;
    if (retiredFilter !== "all") params.retired = retiredFilter;
    try {
      const [{ data: emp }, { data: tms }] = await Promise.all([
        axios.get(`${API}/employees`, { params }),
        axios.get(`${API}/teams`),
      ]);
      setRows(emp);
      setTeams(tms);
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { load(); }, [activeSiteId, status, teamFilter, retiredFilter]); // eslint-disable-line

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (e) => {
    setEditing(e);
    setForm({
      name: e.name || "", tc_no: e.tc_no || "",
      birth_date: e.birth_date || "", birth_place: e.birth_place || "",
      phone: e.phone || "",
      position: e.position || "",
      team_id: e.team_id || "", site_id: e.site_id || "",
      hire_date: e.hire_date || "", end_date: e.end_date || "",
      iban: e.iban || "",
      personnel_file_received: !!e.personnel_file_received,
      personnel_file_path: e.personnel_file_path || "",
      is_retired: !!e.is_retired,
      status: e.status || "aktif", notes: e.notes || "",
    });
    setOpen(true);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      set("personnel_file_path", data.path);
      toast.success("Özlük dosyası yüklendi");
    } catch (e) { toast.error(formatErr(e)); }
    setUploading(false);
  };

  const downloadTemplate = async () => {
    try {
      const res = await axios.get(`${API}/employees/template.xlsx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "personel-sablon.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Şablon indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const exportXlsx = async () => {
    try {
      const params = {};
      if (activeSiteId && activeSiteId !== "all") params.site_id = activeSiteId;
      if (status !== "all") params.status = status;
      if (teamFilter !== "all") params.team_id = teamFilter;
      if (retiredFilter !== "all") params.retired = retiredFilter;
      const res = await axios.get(`${API}/employees/export.xlsx`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `personel-listesi-${new Date().toISOString().slice(0,10)}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Personel listesi indirildi");
    } catch (e) { toast.error(formatErr(e)); }
  };

  const downloadCard = async (emp) => {
    try {
      const res = await axios.get(`${API}/employees/${emp.id}/card.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `personel-kart-${emp.code}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatErr(e)); }
  };

  const importFromExcel = async (file) => {
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/employees/import`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult(data);
      toast.success(`${data.created} personel içe aktarıldı`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    setImporting(false);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("İsim zorunlu"); return; }
    if (!isTcShapeValid(form.tc_no)) { toast.error("TC Kimlik No 11 haneli sayı olmalı"); return; }
    if (!isIbanShapeValid(form.iban)) { toast.error("IBAN formatı geçersiz (TR + 24 hane)"); return; }
    const payload = {
      ...form,
      name: toTitleCaseTR(form.name),
      birth_place: toTitleCaseTR(form.birth_place),
      tc_no: (form.tc_no || "").replace(/\D/g, ""),
      iban: cleanIban(form.iban),
      team_id: form.team_id || null,
      site_id: form.site_id || null,
    };
    // site_id → site_ids senkron (geriye uyumluluk için)
    if (payload.site_id) payload.site_ids = [payload.site_id];
    try {
      if (editing) await axios.put(`${API}/employees/${editing.id}`, payload);
      else await axios.post(`${API}/employees`, payload);
      toast.success(editing ? "Güncellendi" : "Personel eklendi");
      setOpen(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const del = async (e) => {
    const ok = await askConfirm({
      title: "Personeli Sil",
      description: `"${e.name}" verisini silmek istiyor musunuz? Bu işlem geri alınamaz.`,
      destructive: true,
      confirmText: "Sil",
    });
    if (!ok) return;
    try {
      await axios.delete(`${API}/employees/${e.id}`);
      toast.success("Silindi"); load();
    } catch (er) { toast.error(formatErr(er)); }
  };

  const filtered = useMemo(() => {
    const norm = (s) => (s || "").toLocaleLowerCase("tr-TR");
    const nq = norm(q);
    let out = rows;
    if (nq) {
      out = out.filter((r) =>
        norm(r.name).includes(nq) ||
        norm(r.position).includes(nq) ||
        norm(r.tc_no).includes(nq) ||
        norm(r.phone).includes(nq) ||
        norm(r.team_name).includes(nq),
      );
    }
    // Sort
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    out = [...out].sort((a, b) => {
      let va, vb;
      switch (key) {
        case "is_retired":
          va = a.is_retired ? 1 : 0; vb = b.is_retired ? 1 : 0; break;
        case "personnel_file_received":
          va = a.personnel_file_received ? 1 : 0; vb = b.personnel_file_received ? 1 : 0; break;
        case "status":
          va = a.status || ""; vb = b.status || ""; break;
        case "hire_date":
        case "end_date":
        case "tc_no":
        case "phone":
        case "position":
        case "code":
          va = a[key] || ""; vb = b[key] || ""; break;
        case "team_name":
          va = a.team_name || ""; vb = b.team_name || ""; break;
        default:
          va = a.name || ""; vb = b.name || "";
      }
      if (typeof va === "number" && typeof vb === "number") return (va - vb) * mul;
      return String(va).localeCompare(String(vb), "tr") * mul;
    });
    return out;
  }, [rows, q, sort]);

  const toggleSort = (key) => {
    setSort((prev) => {
      if (prev.key !== key) {
        // İlk tıklama: tarihler ve boolean için desc, metin için asc
        const desc = ["hire_date", "end_date", "is_retired", "personnel_file_received"].includes(key);
        return { key, dir: desc ? "desc" : "asc" };
      }
      return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
    });
  };

  const stats = useMemo(() => ({
    total: rows.length,
    active: rows.filter((r) => r.status === "aktif").length,
    passive: rows.filter((r) => r.status === "pasif").length,
    fileMissing: rows.filter((r) => !r.personnel_file_received).length,
  }), [rows]);

  const willBecomePassive = !!form.end_date;

  return (
    <Shell
      title="Personel Yönetimi"
      actions={canManage && (
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={exportXlsx} data-testid="export-xlsx-btn"
                  className="rounded-none sm:rounded-sm border-slate-300">
            <FileXls size={16} className="mr-2 text-emerald-700" /> Excel Dışa Aktar
          </Button>
          <Button variant="outline" onClick={downloadTemplate} data-testid="download-template-btn"
                  className="rounded-none sm:rounded-sm border-slate-300">
            <FileXls size={16} className="mr-2 text-emerald-700" /> Şablon İndir
          </Button>
          <Dialog open={importOpen} onOpenChange={(v) => { setImportOpen(v); if (!v) setImportResult(null); }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="open-import-dialog"
                      className="rounded-none sm:rounded-sm border-slate-300">
                <Upload size={16} className="mr-2 text-[#1B4776]" /> Excel'den İçe Aktar
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:rounded-sm max-w-lg">
              <DialogHeader>
                <DialogTitle className="font-bold font-heading">Toplu Personel İçe Aktar</DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <ol className="list-decimal ml-5 space-y-1 text-slate-700 text-xs">
                  <li>Önce <b>Şablon İndir</b> ile boş şablonu bilgisayarınıza indirin.</li>
                  <li>Şablonu Excel'de açıp <b>3. satırdaki örnek satırı silin</b> ve kendi personel verilerinizi ekleyin.</li>
                  <li>Kaydedip aşağıdan yüklediğinizde tüm satırlar sisteme aktarılır.</li>
                  <li><b>Ekip Adı</b> ve <b>Şantiye Kodu</b> sistemde varsa otomatik eşleşir; yoksa uyarı verilir ama personel yine oluşturulur.</li>
                </ol>
                <div className="p-3 border border-slate-200 bg-slate-50">
                  <input type="file" id="emp-import-file" className="hidden"
                         accept=".xlsx,.xls"
                         onChange={(e) => importFromExcel(e.target.files?.[0])} />
                  <label htmlFor="emp-import-file"
                         data-testid="import-file-input"
                         className={`inline-flex items-center gap-2 px-3 py-2 border border-slate-300 bg-white text-sm cursor-pointer hover:bg-slate-100 ${importing ? "opacity-50 pointer-events-none" : ""}`}>
                    <Upload size={14} />
                    {importing ? "Yükleniyor..." : "Excel Dosyası Seç (.xlsx)"}
                  </label>
                </div>

                {importResult && (
                  <div className="border border-slate-200 p-3 bg-white space-y-2" data-testid="import-result">
                    <div className="flex items-center gap-2 text-emerald-700 font-bold">
                      <CheckCircle size={16} weight="fill" />
                      {importResult.created} personel başarıyla oluşturuldu
                    </div>
                    {importResult.warnings?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1">
                          Uyarılar ({importResult.warnings.length})
                        </div>
                        <ul className="text-xs text-amber-800 space-y-0.5 max-h-32 overflow-y-auto">
                          {importResult.warnings.map((w, i) => (
                            <li key={i}>• Satır {w.row}: {w.msg}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {importResult.skipped?.length > 0 && (
                      <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold text-red-700 mb-1">
                          Atlanan ({importResult.skipped.length})
                        </div>
                        <ul className="text-xs text-red-800 space-y-0.5 max-h-32 overflow-y-auto">
                          {importResult.skipped.map((s, i) => (
                            <li key={i}>• Satır {s.row}: {s.reason}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} data-testid="add-employee"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                <Plus size={16} className="mr-2" /> Yeni Personel
              </Button>
            </DialogTrigger>

          <DialogContent className="rounded-none sm:rounded-sm max-w-3xl max-h-[92vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-bold font-heading">
                {editing ? `Personel Düzenle — ${editing.code}` : "Yeni Personel"}
              </DialogTitle>
            </DialogHeader>

            {willBecomePassive && (
              <div className="flex items-center gap-2 px-3 py-2 border border-amber-300 bg-amber-50 text-amber-800 text-xs" data-testid="passive-warning">
                <Warning size={14} weight="fill" />
                <span>Çıkış tarihi dolu — personel otomatik olarak <b>Pasif</b> duruma alınacaktır.</span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FieldLabel span={2}>Ad Soyad</FieldLabel>
              <div className="md:col-span-2">
                <Input value={form.name} data-testid="emp-name"
                       onChange={(e) => set("name", e.target.value)}
                       onBlur={(e) => set("name", toTitleCaseTR(e.target.value))}
                       className="rounded-none sm:rounded-sm" />
                <div className="text-[10px] text-slate-500 mt-1">
                  Yazım Düzeni: kaydederken otomatik olarak "Yazım Düzeni" biçimine çevrilir (örn: <b>Ahmet Yılmaz</b>).
                </div>
              </div>

              <FieldLabel>T.C. Kimlik No</FieldLabel>
              <FieldLabel>Doğum Tarihi</FieldLabel>
              <div>
                <Input value={form.tc_no} data-testid="emp-tc" maxLength={11}
                       inputMode="numeric" pattern="[0-9]*"
                       onChange={(e) => set("tc_no", e.target.value.replace(/\D/g, "").slice(0, 11))}
                       className="rounded-none sm:rounded-sm font-mono" />
              </div>
              <div>
                <Input type="date" value={form.birth_date} data-testid="emp-birth"
                       onChange={(e) => set("birth_date", e.target.value)}
                       className="rounded-none sm:rounded-sm" />
              </div>

              <FieldLabel>Doğum Yeri</FieldLabel>
              <FieldLabel>Telefon</FieldLabel>
              <div>
                <Input value={form.birth_place} data-testid="emp-birth-place"
                       onChange={(e) => set("birth_place", e.target.value)}
                       onBlur={(e) => set("birth_place", toTitleCaseTR(e.target.value))}
                       className="rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Input value={form.phone} data-testid="emp-phone"
                       onChange={(e) => set("phone", e.target.value)}
                       className="rounded-none sm:rounded-sm" />
              </div>

              <FieldLabel>Meslek Kodu-Mesleği</FieldLabel>
              <FieldLabel>Ekip</FieldLabel>
              <div>
                <MeslekAutocomplete
                  value={form.position}
                  onChange={(v) => set("position", v)}
                  testId="emp-position"
                />
              </div>
              <div>
                <Select value={form.team_id || "__none"} onValueChange={(v) => set("team_id", v === "__none" ? "" : v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="emp-team">
                    <SelectValue placeholder="Ekip seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Ekip Yok —</SelectItem>
                    {teams.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        <span className="font-mono text-xs text-[#1B4776] mr-2">{t.code || "—"}</span>
                        {t.name} <span className="text-slate-400 text-xs">({t.duty})</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <FieldLabel>Çalıştığı Şantiye / Proje</FieldLabel>
              <FieldLabel>&nbsp;</FieldLabel>
              <div>
                <Select value={form.site_id || "__none"} onValueChange={(v) => set("site_id", v === "__none" ? "" : v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="emp-site">
                    <SelectValue placeholder="Şantiye seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none">— Şantiye Yok —</SelectItem>
                    {activeSites.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        <span className="font-mono text-xs mr-2 text-slate-500">{s.code}</span>{s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div />

              <FieldLabel>İşe Giriş Tarihi</FieldLabel>
              <FieldLabel>İşten Ayrılış Tarihi</FieldLabel>
              <div>
                <Input type="date" value={form.hire_date}
                       onChange={(e) => set("hire_date", e.target.value)}
                       className="rounded-none sm:rounded-sm" />
              </div>
              <div>
                <Input type="date" value={form.end_date} data-testid="emp-end-date"
                       onChange={(e) => set("end_date", e.target.value)}
                       className="rounded-none sm:rounded-sm" />
              </div>

              <FieldLabel span={2}>IBAN</FieldLabel>
              <div className="md:col-span-2">
                <Input value={prettyIban(form.iban)} data-testid="emp-iban"
                       onChange={(e) => set("iban", cleanIban(e.target.value))}
                       placeholder="TR__ ____ ____ ____ ____ ____ __"
                       className="rounded-none sm:rounded-sm font-mono" />
                {!isIbanShapeValid(form.iban) && (
                  <div className="text-[10px] text-red-600 mt-1">IBAN 'TR' ile başlamalı ve 24 haneden oluşmalı.</div>
                )}
              </div>

              <FieldLabel span={2}>Özlük Dosyası</FieldLabel>
              <div className="md:col-span-2 flex flex-wrap items-center gap-3 p-3 border border-slate-200 bg-slate-50">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="emp-file-received">
                  <Checkbox checked={form.personnel_file_received}
                            onCheckedChange={(v) => set("personnel_file_received", !!v)} />
                  <span className="text-sm">Fiziki özlük dosyası <b>Alındı</b></span>
                </label>
                <div className="flex items-center gap-2 flex-1 min-w-[240px]">
                  <input type="file" id="emp-file-upload" className="hidden"
                         accept=".pdf,.png,.jpg,.jpeg,.webp"
                         onChange={(e) => handleFileUpload(e.target.files?.[0])} />
                  <label htmlFor="emp-file-upload"
                         className={`inline-flex items-center gap-2 px-3 py-2 border border-slate-300 bg-white text-sm cursor-pointer hover:bg-slate-100 ${uploading ? "opacity-50" : ""}`}>
                    <Paperclip size={14} />
                    {uploading ? "Yükleniyor..." : "Dosya Yükle (PDF/Görsel)"}
                  </label>
                  {form.personnel_file_path && (
                    <a href={`${API}/files/${form.personnel_file_path}`} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-xs text-[#1B4776] underline"
                       data-testid="emp-file-link">
                      <DownloadSimple size={12} /> Yüklenmiş dosyayı görüntüle
                    </a>
                  )}
                </div>
              </div>

              <FieldLabel>Durum</FieldLabel>
              <FieldLabel>Emeklilik</FieldLabel>
              <div>
                <Select value={form.status} onValueChange={(v) => set("status", v)}>
                  <SelectTrigger className="rounded-none sm:rounded-sm" data-testid="emp-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="aktif">Aktif</SelectItem>
                    <SelectItem value="pasif">Pasif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center p-2 border border-slate-200 bg-slate-50 h-10">
                <label className="flex items-center gap-2 cursor-pointer text-sm" data-testid="emp-retired-wrap">
                  <Checkbox checked={form.is_retired} data-testid="emp-is-retired"
                            onCheckedChange={(v) => set("is_retired", !!v)} />
                  <span>Personel <b>emekli</b></span>
                </label>
              </div>

              <FieldLabel span={2}>Not</FieldLabel>
              <div className="md:col-span-2">
                <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)}
                          rows={2} className="rounded-none sm:rounded-sm" />
              </div>
            </div>

            <DialogFooter>
              <Button onClick={save} data-testid="save-employee"
                      className="rounded-none sm:rounded-sm bg-[#1B4776] hover:bg-[#153859] text-white">
                {editing ? "Güncelle" : "Personel Ekle"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      )}
    >
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <StatCard label="Toplam" value={stats.total} tone="slate" />
        <StatCard label="Aktif" value={stats.active} tone="emerald" />
        <StatCard label="Pasif" value={stats.passive} tone="muted" />
        <StatCard label="Dosyası Eksik" value={stats.fileMissing} tone="amber" testId="stat-file-missing" />
      </div>

      {/* Filters */}
      <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm mb-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ara</Label>
          <div className="relative mt-1">
            <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={q} data-testid="emp-search"
                   onChange={(e) => setQ(e.target.value)}
                   placeholder="İsim, TC, telefon, ekip..."
                   className="pl-9 rounded-none sm:rounded-sm" />
          </div>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Durum</Label>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-40" data-testid="emp-filter-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tümü</SelectItem>
              <SelectItem value="aktif">Aktif</SelectItem>
              <SelectItem value="pasif">Pasif</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Ekip</Label>
          <Select value={teamFilter} onValueChange={setTeamFilter}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-56" data-testid="emp-filter-team">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tüm Ekipler</SelectItem>
              {teams.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest font-bold text-slate-600">Emeklilik</Label>
          <Select value={retiredFilter} onValueChange={setRetiredFilter}>
            <SelectTrigger className="mt-1 rounded-none sm:rounded-sm w-44" data-testid="emp-filter-retired">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Hepsi</SelectItem>
              <SelectItem value="yes">Sadece Emekli</SelectItem>
              <SelectItem value="no">Emekli Değil</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Table */}
      <Card className="p-0 rounded-none sm:rounded-sm border-slate-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.2em] text-slate-500 border-b border-slate-200 bg-slate-50">
                <SortableTh label="Kod" col="code" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Ad Soyad" col="name" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="TC" col="tc_no" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Meslek Kodu" col="position" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Ekip" col="team_name" sort={sort} onSort={toggleSort} align="left" />
                <SortableTh label="Özlük Dosyası" col="personnel_file_received" sort={sort} onSort={toggleSort} align="center" />
                <SortableTh label="Çalışma" col="status" sort={sort} onSort={toggleSort} align="center" />
                <SortableTh label="Emeklilik" col="is_retired" sort={sort} onSort={toggleSort} align="center" />
                <SortableTh label="İşe Giriş" col="hire_date" sort={sort} onSort={toggleSort} align="center" />
                <SortableTh label="Çıkış" col="end_date" sort={sort} onSort={toggleSort} align="center" />
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e, i) => (
                <tr key={e.id} className={i % 2 ? "bg-slate-50" : ""} data-testid={`emp-row-${i}`}>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{e.code}</td>
                  <td className="px-4 py-3 font-medium">
                    {e.name}
                    {e.team_leader_id === e.id && (
                      <span className="ml-2 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-[#F7FBE9] text-[#556B10] border-[#95C11F]">Lider</span>
                    )}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{e.tc_no || "-"}</td>
                  <td className="px-4 py-3 text-xs">{e.position || "-"}</td>
                  <td className="px-4 py-3 text-xs text-slate-700">
                    {e.team_name ? (
                      <>
                        <div className="font-medium text-slate-800">{e.team_name}</div>
                        <div className="text-[10px] text-slate-500">{e.team_duty || e.position}</div>
                      </>
                    ) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.personnel_file_received ? (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-emerald-100 text-emerald-800 border-emerald-300">
                        <CheckCircle size={10} weight="fill" /> Alındı
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-amber-100 text-amber-800 border-amber-300">
                        <Warning size={10} weight="fill" /> Alınmadı
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border ${
                      e.status === "aktif" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : "bg-slate-100 text-slate-600 border-slate-300"
                    }`}>{e.status}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {e.is_retired ? (
                      <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5 border bg-purple-100 text-purple-800 border-purple-300">Emekli</span>
                    ) : (
                      <span className="text-[9px] text-slate-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs">{fmtDateTr(e.hire_date)}</td>
                  <td className="px-4 py-3 text-center font-mono text-xs">
                    {e.end_date
                      ? <span className="text-red-700">{fmtDateTr(e.end_date)}</span>
                      : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button onClick={() => downloadCard(e)}
                            data-testid={`emp-card-${i}`}
                            title="Personel Kart PDF"
                            className="w-7 h-7 inline-flex items-center justify-center hover:bg-slate-100">
                      <FilePdf size={13} className="text-[#1B4776]" />
                    </button>
                    {canManage && (
                      <>
                        <button onClick={() => openEdit(e)}
                                data-testid={`emp-edit-${i}`}
                                className="w-7 h-7 inline-flex items-center justify-center hover:bg-slate-100">
                          <PencilSimple size={13} className="text-slate-600" />
                        </button>
                        {isAdmin && (
                          <button onClick={() => del(e)}
                                  data-testid={`emp-delete-${i}`}
                                  className="w-7 h-7 inline-flex items-center justify-center hover:bg-red-50">
                            <TrashSimple size={13} className="text-red-600" />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={11} className="py-16 text-center text-slate-500">
                    <div className="flex flex-col items-center gap-2">
                      <IdentificationCard size={32} weight="duotone" className="text-slate-400" />
                      <div>{q ? "Aramaya uygun kayıt yok." : "Henüz personel yok."}</div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </Shell>
  );
}

// Küçük yardımcı bileşenler
const FieldLabel = ({ children, span = 1 }) => (
  <Label className={`text-xs uppercase tracking-widest font-bold text-slate-700 ${span === 2 ? "md:col-span-2" : ""}`}>
    {children}
  </Label>
);

// Sortable table header
const SortableTh = ({ label, col, sort, onSort, align = "left" }) => {
  const active = sort.key === col;
  const alignClass = align === "center" ? "text-center" : align === "right" ? "text-right" : "text-left";
  return (
    <th className={`${alignClass} px-4 py-3 font-bold cursor-pointer select-none hover:bg-slate-100 transition-colors`}
        data-testid={`sort-${col}`}
        onClick={() => onSort(col)}>
      <span className="inline-flex items-center gap-0.5">
        {label}
        {!active && <CaretUpDown size={10} className="text-slate-400" />}
        {active && sort.dir === "asc" && <CaretUp size={10} weight="fill" className="text-[#1B4776]" />}
        {active && sort.dir === "desc" && <CaretDown size={10} weight="fill" className="text-[#1B4776]" />}
      </span>
    </th>
  );
};

// GG.AA.YYYY formatter for list cells
const fmtDateTr = (iso) => {
  if (!iso) return "—";
  const s = String(iso).slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

const StatCard = ({ label, value, tone = "slate", testId }) => {
  const colorMap = {
    slate: "text-slate-900",
    emerald: "text-emerald-700",
    muted: "text-slate-500",
    amber: "text-amber-700",
  };
  return (
    <Card className="p-4 rounded-none sm:rounded-sm border-slate-200 shadow-sm" data-testid={testId}>
      <div className={`text-[10px] uppercase tracking-widest font-bold ${tone === "emerald" ? "text-emerald-700" : tone === "amber" ? "text-amber-700" : "text-slate-500"}`}>{label}</div>
      <div className={`text-2xl font-bold font-heading ${colorMap[tone]}`}>{value}</div>
    </Card>
  );
};
